import React, { useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "./ui/button";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Loader2, Trash2, Copy } from 'lucide-react';
import { createAgent, registerAgent } from '../services/apiClient';
import { getAuthHeaders } from '../utils/getAuthHeaders';
import { getErrorMessage } from '../utils/errors';
import { config } from '../config';

const AgentRegistrationInstructions = ({ agentId, tenantId, registrationInfo, onCopy }) => {
  if (!registrationInfo) {
    return <p className="text-sm text-gray-600">Click "Register" to view deployment instructions.</p>;
  }

  const envVars = {
    TENANT_ID: tenantId,
    AGENT_ID: agentId,
    BACKEND_API_URL: config.apiUrl,
    ...(registrationInfo?.env || {}),
  };

  const envSnippet = Object.entries(envVars)
    .map(([key, value]) => `${key}=${value ?? '<set-value>'}`)
    .join('\n');

  const secretValue = envVars.AGENT_SHARED_SECRET;
  const secretIsProvided = Boolean(secretValue && secretValue !== '<set-agent-shared-secret>');
  const secretRequired = registrationInfo?.agent_token_required ?? secretIsProvided;

  return (
    <div className="mt-4 p-4 bg-gray-100 rounded-md text-sm space-y-3">
      <h4 className="font-semibold text-gray-800">Agent Ready for Deployment</h4>
      <p>
        Deploy the Dockerized agent inside your network with the environment variables below. Add your
        database credentials and ensure <code>AGENT_SHARED_SECRET</code> matches the shared secret configured in the backend.
      </p>

      <pre className="bg-white border rounded p-3 font-mono text-xs overflow-x-auto">{envSnippet}</pre>

      <Button
        onClick={() => onCopy(envSnippet)}
        variant="outline"
        size="sm"
        className="inline-flex items-center gap-1"
      >
        <Copy className="h-3 w-3" /> Copy env snippet
      </Button>

      {registrationInfo.message && (
        <p className="text-xs text-gray-600">{registrationInfo.message}</p>
      )}

      {!secretRequired && (
        <p className="text-xs text-amber-600">
          Shared-secret auth is currently disabled. Set <code>AGENT_SHARED_SECRET</code> on the backend and restart the agent to enforce authentication.
        </p>
      )}

      {secretRequired && !secretIsProvided && (
        <p className="text-xs text-amber-600">
          Update <code>AGENT_SHARED_SECRET</code> with the value issued by the backend before starting the agent container.
        </p>
      )}
    </div>
  );
};

export default function AgentManagement({
  initialAgents = [],
  tenantId,
  onAgentsUpdate,
  onClose,
  isLoading: isParentLoading,
  handleDeleteAgentClick,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [registrationError, setRegistrationError] = useState({});
  const [registrationDetails, setRegistrationDetails] = useState({});
  const [registeringAgentId, setRegisteringAgentId] = useState(null);

  const { getAccessTokenSilently } = useAuth0();

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };

  const handleCreateAgent = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const headers = getAuthHeaders(await getAccessTokenSilently());
      await createAgent(tenantId, headers);
      onAgentsUpdate();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRegisterAgent = async (agentId) => {
    setRegisteringAgentId(agentId);
    setRegistrationError((prev) => ({ ...prev, [agentId]: null }));
    try {
      const headers = getAuthHeaders(await getAccessTokenSilently());
      const registrationInfo = await registerAgent(agentId, headers);
      setRegistrationDetails((prev) => ({ ...prev, [agentId]: registrationInfo }));
      onAgentsUpdate();
    } catch (err) {
      setRegistrationError((prev) => ({ ...prev, [agentId]: getErrorMessage(err) }));
    } finally {
      setRegisteringAgentId(null);
    }
  };

  const renderAgentRow = (agent) => {
    return (
      <div key={agent.agent_id} className="border rounded-lg p-4 bg-white shadow-sm">
        <div className="flex justify-between items-start gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-800 break-words">
              Agent ID: <code className="text-xs bg-gray-100 p-1 rounded break-all">{agent.agent_id}</code>
            </p>
            <p className={`text-xs ${agent.status === 'ONLINE' ? 'text-green-600' : 'text-gray-500'}`}>
              Status: {agent.status || 'UNKNOWN'}
            </p>
            <p className="text-xs text-gray-500">
              Created: {new Date(agent.created_at).toLocaleString()}
            </p>
            {agent.last_seen && (
              <p className="text-xs text-gray-500">
                Last Seen: {new Date(agent.last_seen).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {agent.status === 'CREATED' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRegisterAgent(agent.agent_id)}
                disabled={registeringAgentId === agent.agent_id}
                className="text-xs"
              >
                {registeringAgentId === agent.agent_id && (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                )}
                {registeringAgentId === agent.agent_id ? 'Registering…' : 'Register'}
              </Button>
            )}
            {handleDeleteAgentClick && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteAgentClick(agent.agent_id)}
                className="text-xs text-red-600 hover:text-red-800 p-1"
                title="Delete Agent"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {registrationError[agent.agent_id] && (
          <p className="text-xs text-red-600 mt-1">{registrationError[agent.agent_id]}</p>
        )}

        {registrationDetails[agent.agent_id] && (
          <AgentRegistrationInstructions
            agentId={agent.agent_id}
            tenantId={tenantId}
            registrationInfo={registrationDetails[agent.agent_id]}
            onCopy={copyToClipboard}
          />
        )}
      </div>
    );
  };

  const isLoading = isParentLoading || isCreating;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Database Agents</h3>
          <p className="text-sm text-gray-600">
            Agents poll the Tenxer backend over HTTPS and execute read-only SQL against your database.
          </p>
        </div>
        <Button
          onClick={handleCreateAgent}
          disabled={isCreating}
          className="flex items-center gap-2"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating…
            </>
          ) : (
            'Create Agent'
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading agents…
        </div>
      ) : initialAgents.length === 0 ? (
        <div className="text-sm text-gray-600">No agents created yet.</div>
      ) : (
        <div className="space-y-3">
          {initialAgents.map(renderAgentRow)}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
